import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NotificationDto, NotificationListDto } from '@fyr/shared';
import { api } from '../api';
import { stamp } from '../format';
import { nativeBridge } from '../native';

const ICON: Record<NotificationDto['kind'], string> = {
  project_added: '➕',
  action_assigned: '☑',
  mentioned: '@',
  review_requested: '✎',
  item_changed: '↻',
  chat_message: '💬',
  training_assigned: '🎓',
  connection: '🤝',
  shared: '↗',
  someone_joined: '👋',
};

/** §19 M9.1 the bell: your notifications in this organisation (newest first), unread count, mark read. */
export function NotificationBell() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationListDto>('/notifications'),
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  const read = async (ids: string[] | 'all') => {
    await api.post('/notifications/read', { ids });
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const unread = q.data?.unread ?? 0;
  // #155.2 in the app: the icon's badge follows the bell
  useEffect(() => {
    if (q.data)
      void nativeBridge()
        ?.Plugins.FyrServer?.setBadge?.({ count: unread })
        .catch(() => undefined);
  }, [unread, q.data]);
  return (
    <div className="bell" ref={box}>
      <button
        className="btn icon-btn bell-btn"
        aria-label={t('notify.bell', { count: unread })}
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          if (!open) void q.refetch();
        }}
      >
        <span aria-hidden>🔔</span>
        {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="bell-panel" role="dialog" aria-label={t('notify.title')}>
          <div className="bell-head">
            <strong>{t('notify.title')}</strong>
            <span className="spacer" />
            {unread > 0 && (
              <button className="link-btn small" onClick={() => void read('all')}>
                {t('notify.markAll')}
              </button>
            )}
          </div>
          {!q.data?.items.length && <p className="muted small bell-empty">{t('notify.empty')}</p>}
          <ul>
            {q.data?.items.map((n) => (
              <li key={n.id} className={n.readAt ? '' : 'unread'}>
                <button
                  onClick={async () => {
                    setOpen(false);
                    if (!n.readAt) await read([n.id]);
                    if (n.link) nav(n.link);
                  }}
                >
                  <span className="bell-icon" aria-hidden>
                    {ICON[n.kind]}
                  </span>
                  <span className="bell-text">
                    <span className="bell-title">{n.title}</span>
                    {n.body && <span className="bell-body">{n.body}</span>}
                    <span className="muted small">
                      {[n.projectName, stamp(n.createdAt)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="bell-foot">
            <button
              className="link-btn small"
              onClick={() => (setOpen(false), nav('/profile#notifications'))}
            >
              {t('notify.settings')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
